<script lang="ts">
  import {
    AlertCircle,
    AlertTriangle,
    ArrowLeft,
    CheckCircle2,
    HelpCircle,
    Info,
    Loader2,
    XCircle,
  } from 'lucide-svelte';
  import type {
    ParityStatus,
    WarmupEngineMetric,
    WarmupParityEnriched,
  } from '@rhdashboard-vnext/shared';
  import { DEFAULT_PARITY_TOLERANCES } from '@rhdashboard-vnext/shared';
  import { fetchWarmupParity } from '$lib/api/parity';
  import { Badge } from '$lib/components/ui/badge';
  import { Button } from '$lib/components/ui/button';
  import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogTitle,
  } from '$lib/components/ui/dialog';
  import CopyableSessionId from './CopyableSessionId.svelte';

  // ============================================================
  // <WarmupParityModal>  -  Fase 6.
  //
  // Modal del flujo "click en badge WP:*" o "click en status del Parity
  // Check modal". Lee el documento warmupParityResults via GET
  // /api/parity/:sessionId/warmup (Mongo direct + engine state enrichment).
  //
  // Layout (espejo del v1 line 11003+):
  //   1. Header: status badge + comparisonMode (snapshot|preflight) label.
  //   2. Banner amarillo si fallbackReason existe (explica modo legacy).
  //   3. Main table 7-col: Metric | Origin | Preflight@end | Extension |
  //      Diff | Tolerance | Pass. Diff/Pass siempre Origin vs Preflight
  //      (apples-to-apples), nunca contra Extension.
  //   4. Sub-table "Legacy 2-col" condicional (solo si !isSnapshot &&
  //      !useFullPreflightAsAtEnd).
  //   5. Engine State table (5 metricas: ATR, Box Price, Block Count,
  //      Box Size, Candle Count) con tooltips de tolerancia.
  //
  // Fixes vs v1:
  //   - safe formatters (audit BUG #6: data.live.pnl.toFixed crashea con null)
  //   - DEFAULT_PARITY_TOLERANCES del shared (audit BUG #1: tolerancias
  //     hardcoded duplicadas) — UI lee de single source of truth.
  // ============================================================

  interface Props {
    open: boolean;
    sessionId: string | null;
    onClose: () => void;
    /** Si se provee, renderea un boton "Back to Parity Check" en el footer.
     * Usado cuando el modal se abrio desde el badge de status del
     * ParityCheckModal — el operador puede volver sin reabrirlo. */
    onBack?: () => void;
  }

  let { open = $bindable(), sessionId, onClose, onBack }: Props = $props();

  let loading = $state(false);
  let error = $state<string | null>(null);
  let data = $state<WarmupParityEnriched | null>(null);
  let abortController: AbortController | null = null;

  $effect(() => {
    if (open && sessionId) {
      void load();
    } else if (!open) {
      reset();
    }
  });

  async function load(): Promise<void> {
    if (!sessionId) return;
    cleanup();
    loading = true;
    error = null;
    data = null;
    abortController = new AbortController();
    try {
      data = await fetchWarmupParity(sessionId, abortController.signal);
    } catch (err) {
      if (err instanceof Error && err.name === 'AbortError') return;
      error = err instanceof Error ? err.message : 'Unknown error';
    } finally {
      loading = false;
    }
  }

  function cleanup(): void {
    if (abortController) abortController.abort();
    abortController = null;
  }

  function reset(): void {
    cleanup();
    loading = false;
    error = null;
    data = null;
  }

  // ----- Helpers de read seguros sobre el result raw -----
  function num(obj: unknown, ...path: string[]): number | null {
    let cur: unknown = obj;
    for (const k of path) {
      if (cur == null || typeof cur !== 'object') return null;
      cur = (cur as Record<string, unknown>)[k];
    }
    return typeof cur === 'number' && Number.isFinite(cur) ? cur : null;
  }

  // ----- Formatters safe -----
  function fmtMoney(n: number | null): string {
    if (n == null) return '—';
    if (n === 0) return '$0.00';
    const sign = n > 0 ? '+' : '-';
    return `${sign}$${Math.abs(n).toFixed(2)}`;
  }
  function fmtPct(n: number | null): string {
    // n viene en fraccion 0..1 (e.g. 0.012 = 1.2%) segun audit BUG #3.
    if (n == null) return '—';
    return `${(n * 100).toFixed(1)}%`;
  }
  function fmtPF(n: number | null): string {
    if (n == null) return '—';
    if (!Number.isFinite(n)) return '∞';
    return n.toFixed(2);
  }
  function fmtInt(n: number | null): string {
    if (n == null) return '—';
    return n.toString();
  }
  function fmtAtr(n: number | null): string {
    if (n == null) return '—';
    return n.toFixed(4);
  }

  // ----- Status visual -----
  function statusInfo(s: ParityStatus | string | undefined) {
    if (s === 'OK')
      return {
        cls: 'border-success/40 bg-success/15 text-success',
        icon: CheckCircle2,
        label: 'OK',
      };
    if (s === 'DRIFT')
      return {
        cls: 'border-warning/40 bg-warning/15 text-warning',
        icon: AlertTriangle,
        label: 'DRIFT',
      };
    if (s === 'CRITICAL')
      return {
        cls: 'border-destructive/40 bg-destructive/15 text-destructive',
        icon: XCircle,
        label: 'CRITICAL',
      };
    return {
      cls: 'border-muted/40 bg-muted/15 text-muted-foreground',
      icon: HelpCircle,
      label: s ?? '—',
    };
  }

  // ----- Derived helpers sobre data.result -----
  // El audit explica que `comparisonMode` decide layout:
  //   - 'snapshot' -> apples-to-apples, hay snapshot (exact at origin-end)
  //   - 'preflight' -> legacy, no hay snapshot; preflight es preflight full
  let isSnapshot = $derived(data?.result.comparisonMode === 'snapshot');
  let fallbackReason = $derived(data?.result.fallbackReason ?? null);
  // FIXES_QUEUE #6 del audit: si fallbackReason es cutoff_in_no_trading_window
  // promocionamos preflight (full range) a la columna "@origin end".
  let useFullPreflightAsAtEnd = $derived(
    fallbackReason === 'cutoff_in_no_trading_window',
  );

  function fallbackMessage(reason: string | null): string {
    if (reason === 'cutoff_in_no_trading_window') {
      return 'Origin-end timestamp landed in a filtered-out window (e.g. Friday close + trading-hours filter). Showing preflight full range as the at-origin-end comparison.';
    }
    if (reason === 'legacy_no_snapshot') {
      return 'Session pre-dates the snapshot feature. Comparing legacy preflight (full range) to origin — informational only.';
    }
    if (reason) return `Fallback mode: ${reason}`;
    return '';
  }

  // ----- Origin column data (siempre del result.origin) -----
  let originData = $derived.by(() => {
    const o = data?.result.origin as Record<string, unknown> | undefined;
    if (!o) return null;
    return {
      totalPnL: num(o, 'totalPnL'),
      numTrades: num(o, 'numTrades'),
      wins: num(o, 'wins'),
      losses: num(o, 'losses'),
      winRate: num(o, 'winRate'),
      profitFactor: num(o, 'profitFactor'),
    };
  });

  // ----- At-origin-end column data: snapshot si existe, sino preflight si
  // useFullPreflightAsAtEnd, sino preflight basico (legacy) -----
  let atEndData = $derived.by(() => {
    if (!data) return null;
    if (isSnapshot) {
      const s = data.result.snapshot as Record<string, unknown> | undefined;
      if (!s) return null;
      return {
        totalPnL: num(s, 'totalPnL'),
        numTrades: num(s, 'tradeCount'),
        wins: num(s, 'wins'),
        losses: num(s, 'losses'),
        winRate: num(s, 'winRate'),
        profitFactor: num(s, 'profitFactor'),
      };
    }
    const src = useFullPreflightAsAtEnd
      ? (data.result.preflightFull as Record<string, unknown> | undefined)
      : (data.result.preflight as Record<string, unknown> | undefined);
    if (!src) return null;
    return {
      totalPnL: num(src, 'totalPnL'),
      numTrades: num(src, 'numTrades'),
      wins: num(src, 'wins'),
      losses: num(src, 'losses'),
      winRate: num(src, 'winRate'),
      profitFactor: num(src, 'profitFactor'),
    };
  });

  // ----- Extension column: computed FROM aditivos (no resta de ratios) -----
  let extensionData = $derived.by(() => {
    if (!data || !isSnapshot) return null;
    const snap = data.result.snapshot as Record<string, unknown> | undefined;
    const full = data.result.preflightFull as Record<string, unknown> | undefined;
    if (!snap || !full) return null;
    const extTrades = (num(full, 'numTrades') ?? 0) - (num(snap, 'tradeCount') ?? 0);
    const extWins = (num(full, 'wins') ?? 0) - (num(snap, 'wins') ?? 0);
    const extLosses = (num(full, 'losses') ?? 0) - (num(snap, 'losses') ?? 0);
    const extPnL = (num(full, 'totalPnL') ?? 0) - (num(snap, 'totalPnL') ?? 0);
    const extGrossWin = (num(full, 'grossWin') ?? 0) - (num(snap, 'grossWin') ?? 0);
    const extGrossLoss = (num(full, 'grossLoss') ?? 0) - (num(snap, 'grossLoss') ?? 0);
    const winRate = extTrades > 0 ? extWins / extTrades : null;
    const profitFactor = extGrossLoss > 0
      ? extGrossWin / extGrossLoss
      : extGrossWin > 0
        ? Number.POSITIVE_INFINITY
        : null;
    return {
      totalPnL: extPnL,
      numTrades: extTrades,
      wins: extWins,
      losses: extLosses,
      winRate,
      profitFactor,
    };
  });

  // ----- Diff (Origin vs at-end) y pass por metrica -----
  function diffAndPass(metric: keyof typeof DEFAULT_PARITY_TOLERANCES) {
    if (!originData || !atEndData) return { diff: null as number | null, pass: null };
    let origin: number | null = null;
    let end: number | null = null;
    if (metric === 'totalPnL') {
      origin = originData.totalPnL;
      end = atEndData.totalPnL;
    } else if (metric === 'numTrades') {
      origin = originData.numTrades;
      end = atEndData.numTrades;
    } else if (metric === 'wins') {
      origin = originData.wins;
      end = atEndData.wins;
    } else if (metric === 'losses') {
      origin = originData.losses;
      end = atEndData.losses;
    } else if (metric === 'winRate') {
      origin = originData.winRate;
      end = atEndData.winRate;
    } else if (metric === 'profitFactor') {
      origin = originData.profitFactor;
      end = atEndData.profitFactor;
    }
    if (origin == null || end == null) return { diff: null, pass: null };
    const diff = end - origin;
    const tol = DEFAULT_PARITY_TOLERANCES[metric];
    const pass = Math.abs(diff) <= tol;
    return { diff, pass };
  }

  function passIcon(pass: boolean | null) {
    if (pass === null) return null;
    return pass ? CheckCircle2 : XCircle;
  }

  // ----- Engine state diffs -----
  const ENGINE_TOLERANCES = {
    atr: 0.01,
    boxPrice: 0.5,
    blockCount: 0, // exact match
    boxSize: 0.01,
  } as const;

  function engineDiff(
    a: WarmupEngineMetric | null,
    b: WarmupEngineMetric | null,
    field: keyof WarmupEngineMetric,
  ): { diff: number | null; pass: boolean | null } {
    if (!a || !b) return { diff: null, pass: null };
    const av = a[field];
    const bv = b[field];
    if (av == null || bv == null) return { diff: null, pass: null };
    const diff = av - bv;
    const tol = field in ENGINE_TOLERANCES ? ENGINE_TOLERANCES[field as keyof typeof ENGINE_TOLERANCES] : 0;
    const pass = Math.abs(diff) <= tol;
    return { diff, pass };
  }
</script>

<Dialog bind:open onOpenChange={(o) => !o && onClose()}>
  <DialogContent class="max-h-[90vh] max-w-5xl overflow-y-auto">
    <!-- Header: mismo patron visual que ParityCheckModal y resto del site. -->
    <DialogTitle class="flex flex-wrap items-center gap-3 pr-8">
      <span class="text-gold font-serif text-lg font-semibold tracking-tight">
        Warmup Parity
      </span>
      {#if sessionId}
        <CopyableSessionId sessionId={sessionId} size="md" stopPropagation={false} />
      {/if}
      {#if data?.result.status}
        {@const si = statusInfo(data.result.status as string)}
        <span
          class={[
            'inline-flex items-center gap-1 rounded-md border px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider',
            si.cls,
          ].join(' ')}
        >
          <si.icon class="size-3" />
          {si.label}
        </span>
      {/if}
      {#if data}
        <Badge variant="outline" class="text-[10px] uppercase">
          {isSnapshot ? 'Snapshot (exact)' : 'Preflight (legacy)'}
        </Badge>
      {/if}
    </DialogTitle>
    <DialogDescription class="sr-only">
      Warmup parity comparison between backtest origin and session launch.
    </DialogDescription>

    {#if loading}
      <div class="flex items-center justify-center gap-2 py-8">
        <Loader2 class="text-primary size-6 animate-spin" />
        <p class="text-muted-foreground text-sm">Loading warmup parity…</p>
      </div>
    {/if}

    {#if error && !loading}
      <div
        role="alert"
        class="border-destructive/40 bg-destructive/10 text-destructive flex items-start gap-2 rounded-md border px-3 py-2 text-sm"
      >
        <AlertCircle class="mt-0.5 size-4 shrink-0" />
        <div class="flex-1">
          <p class="font-medium">Could not load warmup parity</p>
          <p class="text-xs opacity-80">{error}</p>
        </div>
      </div>
    {/if}

    {#if data && !loading}
      <!-- Banner amarillo si fallbackReason existe -->
      {#if fallbackReason}
        <div
          class="border-warning/40 bg-warning/10 text-warning flex items-start gap-2 rounded-md border px-3 py-2 text-sm"
        >
          <Info class="mt-0.5 size-4 shrink-0" />
          <p class="text-xs">{fallbackMessage(fallbackReason as string)}</p>
        </div>
      {/if}

      <!-- Main 7-col table -->
      <div class="overflow-x-auto rounded-md border">
        <table class="w-full text-sm">
          <thead class="bg-muted/40 text-muted-foreground">
            <tr>
              <th class="px-3 py-2 text-left font-normal uppercase tracking-wider text-xs">Metric</th>
              <th class="px-3 py-2 text-right font-normal uppercase tracking-wider text-xs">Origin</th>
              <th class="px-3 py-2 text-right font-normal uppercase tracking-wider text-xs">
                {useFullPreflightAsAtEnd ? 'Preflight (full)' : isSnapshot ? 'Snapshot' : 'Preflight'}
              </th>
              {#if isSnapshot}
                <th class="px-3 py-2 text-right font-normal uppercase tracking-wider text-xs">Extension</th>
              {/if}
              <th class="px-3 py-2 text-right font-normal uppercase tracking-wider text-xs">Diff</th>
              <th class="px-3 py-2 text-right font-normal uppercase tracking-wider text-xs">Tol</th>
              <th class="px-3 py-2 text-center font-normal uppercase tracking-wider text-xs">Pass</th>
            </tr>
          </thead>
          <tbody class="font-mono tabular-nums">
            {#each [
              { key: 'totalPnL', label: 'Total P&L', fmt: fmtMoney, tolDisplay: `±$${DEFAULT_PARITY_TOLERANCES.totalPnL}` },
              { key: 'numTrades', label: 'Trades', fmt: fmtInt, tolDisplay: `±${DEFAULT_PARITY_TOLERANCES.numTrades}` },
              { key: 'wins', label: 'Wins', fmt: fmtInt, tolDisplay: `±${DEFAULT_PARITY_TOLERANCES.wins}` },
              { key: 'losses', label: 'Losses', fmt: fmtInt, tolDisplay: `±${DEFAULT_PARITY_TOLERANCES.losses}` },
              { key: 'winRate', label: 'Win Rate', fmt: fmtPct, tolDisplay: `±${(DEFAULT_PARITY_TOLERANCES.winRate * 100).toFixed(1)}%` },
              { key: 'profitFactor', label: 'Profit Factor', fmt: fmtPF, tolDisplay: `±${DEFAULT_PARITY_TOLERANCES.profitFactor}` },
            ] as row (row.key)}
              {@const k = row.key as keyof typeof DEFAULT_PARITY_TOLERANCES}
              {@const dp = diffAndPass(k)}
              {@const PassIcon = passIcon(dp.pass)}
              <tr class="border-t">
                <td class="px-3 py-2 font-sans">{row.label}</td>
                <td class="px-3 py-2 text-right">
                  {row.fmt(originData ? (originData[k as keyof typeof originData] as number | null) : null)}
                </td>
                <td class="px-3 py-2 text-right">
                  {row.fmt(atEndData ? (atEndData[k as keyof typeof atEndData] as number | null) : null)}
                </td>
                {#if isSnapshot}
                  <td class="text-muted-foreground px-3 py-2 text-right">
                    {row.fmt(extensionData ? (extensionData[k as keyof typeof extensionData] as number | null) : null)}
                  </td>
                {/if}
                <td class={['px-3 py-2 text-right', dp.pass === false && 'text-destructive'].filter(Boolean).join(' ')}>
                  {row.fmt(dp.diff)}
                </td>
                <td class="text-muted-foreground px-3 py-2 text-right text-xs">{row.tolDisplay}</td>
                <td class="px-3 py-2 text-center">
                  {#if PassIcon}
                    <PassIcon class={['inline size-4', dp.pass ? 'text-success' : 'text-destructive'].filter(Boolean).join(' ')} />
                  {:else}
                    <span class="text-muted-foreground">—</span>
                  {/if}
                </td>
              </tr>
            {/each}
          </tbody>
        </table>
      </div>

      <!-- Engine State table -->
      <div>
        <h3 class="mb-2 text-sm font-semibold">Engine State</h3>
        <div class="overflow-x-auto rounded-md border">
          <table class="w-full text-sm">
            <thead class="bg-muted/40 text-muted-foreground">
              <tr>
                <th class="px-3 py-2 text-left font-normal uppercase tracking-wider text-xs">Metric</th>
                <th class="px-3 py-2 text-right font-normal uppercase tracking-wider text-xs">
                  End of Single
                  {#if data.engineState.originUnavailableReason}
                    <span class="text-muted-foreground/70" title={data.engineState.originUnavailableReason}>
                      <HelpCircle class="ml-1 inline size-3" />
                    </span>
                  {/if}
                </th>
                <th class="px-3 py-2 text-right font-normal uppercase tracking-wider text-xs">Session Start</th>
                <th class="px-3 py-2 text-right font-normal uppercase tracking-wider text-xs">Diff</th>
                <th class="px-3 py-2 text-right font-normal uppercase tracking-wider text-xs">Tol</th>
                <th class="px-3 py-2 text-center font-normal uppercase tracking-wider text-xs">Pass</th>
              </tr>
            </thead>
            <tbody class="font-mono tabular-nums">
              {#each [
                { key: 'atr', label: 'ATR', fmt: fmtAtr, tol: '≤ 0.01' },
                { key: 'boxPrice', label: 'Box Price', fmt: (n: number | null) => n == null ? '—' : n.toFixed(2), tol: '≤ 0.50' },
                { key: 'blockCount', label: 'Block Count', fmt: fmtInt, tol: 'Exact' },
                { key: 'boxSize', label: 'Box Size', fmt: (n: number | null) => n == null ? '—' : n.toFixed(4), tol: '≤ 0.01' },
              ] as row (row.key)}
                {@const k = row.key as keyof WarmupEngineMetric}
                {@const ed = engineDiff(data.engineState.origin, data.engineState.preflight, k)}
                {@const PassIcon = passIcon(ed.pass)}
                <tr class="border-t">
                  <td class="px-3 py-2 font-sans">{row.label}</td>
                  <td class="px-3 py-2 text-right">
                    {data.engineState.origin
                      ? row.fmt(data.engineState.origin[k] as number | null)
                      : 'N/A'}
                  </td>
                  <td class="px-3 py-2 text-right">
                    {data.engineState.preflight
                      ? row.fmt(data.engineState.preflight[k] as number | null)
                      : '—'}
                  </td>
                  <td class={['px-3 py-2 text-right', ed.pass === false && 'text-destructive'].filter(Boolean).join(' ')}>
                    {row.fmt(ed.diff)}
                  </td>
                  <td class="text-muted-foreground px-3 py-2 text-right text-xs">{row.tol}</td>
                  <td class="px-3 py-2 text-center">
                    {#if PassIcon}
                      <PassIcon class={['inline size-4', ed.pass ? 'text-success' : 'text-destructive'].filter(Boolean).join(' ')} />
                    {:else}
                      <span class="text-muted-foreground">—</span>
                    {/if}
                  </td>
                </tr>
              {/each}
            </tbody>
          </table>
        </div>
      </div>
    {/if}

    <div class="flex items-center justify-between gap-2 pt-2">
      <div>
        {#if onBack}
          <Button variant="ghost" size="sm" onclick={onBack}>
            <ArrowLeft class="size-3.5" />
            Back to Parity Check
          </Button>
        {/if}
      </div>
      <Button variant="outline" size="sm" onclick={onClose}>Close</Button>
    </div>
  </DialogContent>
</Dialog>
